import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSearchTracksQuery } from 'services/tracks/tracks';
import { AutocompleteWithEdit } from 'components/common/AutocompleteWithEdit/AutocompleteWithEdit';
import { useRootTranslation } from 'utils/hooks';
import { ResolverOption } from '../reducer';
import { RESOLVER_GUTTER_WIDTH } from '../constants';

const rootT = 'drawers.tracksImportResolver';

/* The control overhangs left out of the field container into the RESOLVER_GRID gutter
   track (see constants.ts): far enough that it clears the gutter's own width plus the
   columnGap on either side of it, landing centered in that gutter rather than against
   the QwireTracksFields column edge. */
const COPY_CONTROL_LEFT_OFFSET = `-${RESOLVER_GUTTER_WIDTH + 24}px`;

type TrackTitleSelectProps = {
  value: ResolverOption | null;
  libraryName: string | null;
  vendorTitle: string;
  placeholder: string;
  disabled: boolean;
  onChange: (option: ResolverOption | null) => void;
  onCreate: (name: string) => void;
  onClear: () => void;
  /* Row's Library field is a pending "Create New X" -- a brand-new library has nothing in
     the catalog yet, so the search must be skipped outright (not merely filtered): an ES
     fuzzy match against libraryTerm could otherwise still surface unrelated hits. */
  isPendingNewLibrary?: boolean;
};

const TrackTitleSelect = ({
  value,
  libraryName,
  vendorTitle,
  placeholder,
  disabled,
  onChange,
  onCreate,
  onClear,
  isPendingNewLibrary = false,
}: TrackTitleSelectProps) => {
  const t = useRootTranslation(rootT);
  const [term, setTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const { data, isFetching } = useSearchTracksQuery(
    {
      types: { library: true, sound_effect: true },
      filters: { titleTerm: term, ...(libraryName && { libraryTerm: libraryName }) },
      page: 1,
      limit: 20,
    },
    { skip: !term || isPendingNewLibrary },
  );

  const debouncedSetTerm = useDebouncedCallback((newTerm: string) => {
    setTerm(newTerm);
  }, 300);

  const handleInputChange = (_, newInputValue: string) => {
    setInputValue(newInputValue ?? '');
    debouncedSetTerm(newInputValue ?? '');
  };

  /* Copies the imported track title into the search field and kicks off the search
     immediately — the debounce above is only meant to smooth out keystrokes (QW-158). */
  const handleCopyVendorTitle = () => {
    setInputValue(vendorTitle);
    setTerm(vendorTitle);
  };

  /* RTK Query keeps the last successful `data` even once `skip` flips true — so after
     searching under an existing library and then switching the row to a pending new one,
     the stale results would still render. A pending new library can't own tracks: gate
     the options themselves, not just the request. */
  const options = isPendingNewLibrary ? [] : (data?.data ?? []).map((track) => ({ id: track.id, name: track.title }));

  return (
    /* Position:relative anchor for the copy control: it stays vertically pinned to this
       (the Track Title input's) container while overhanging left into the RESOLVER_GRID
       gutter — out of normal flow, so the search field keeps its full column width. */
    <Box sx={{ position: 'relative' }}>
      {libraryName && vendorTitle && (
        <Tooltip title={t('fields.copyTrackTitle')}>
          <IconButton
            size="small"
            aria-label={t('fields.copyTrackTitle')}
            onClick={handleCopyVendorTitle}
            sx={{
              position: 'absolute',
              top: '50%',
              left: COPY_CONTROL_LEFT_OFFSET,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              p: 0.5,
            }}
          >
            <ContentCopyIcon fontSize="small" />
            <ArrowForwardIcon fontSize="small" sx={{ ml: '-2px' }} />
          </IconButton>
        </Tooltip>
      )}
      <AutocompleteWithEdit
        options={options}
        value={value}
        inputValue={inputValue}
        onChange={onChange}
        onCreate={onCreate}
        onClear={onClear}
        onInputChange={handleInputChange}
        loading={isFetching}
        disabled={disabled}
        size="small"
        separateCreateOption
        inputProps={{ placeholder }}
      />
    </Box>
  );
};

export default TrackTitleSelect;
